/** The head of a screen's body: an optional title, a one-line subtitle, a tabs slot and an action
 * slot, in one row that wraps.
 *
 * **The shell already prints the screen's `<h1>` in the top bar** (`AppShell`), so the title here
 * defaults to an `h2` and is optional: most screens want only the subtitle and the actions. The
 * `tabs` slot takes the product's one tablist (`RecordTabs`) rather than drawing a second; this
 * component places it and owns none of its behaviour. The subtitle is a sibling of the heading for
 * the reason `Panel` gives: inside it, it would join the accessible name.
 */

import type { ReactNode } from "react";

import styles from "./PageHeader.module.css";

export function PageHeader({
  title,
  as: Heading = "h2",
  subtitle,
  tabs,
  actions,
}: {
  title?: string;
  as?: "h1" | "h2" | "h3";
  subtitle?: ReactNode;
  tabs?: ReactNode;
  actions?: ReactNode;
}) {
  if (!title && !subtitle && !tabs && !actions) return null;
  return (
    <header className={styles.head} data-testid="page-header">
      <div className={styles.text}>
        {title ? <Heading className={styles.title}>{title}</Heading> : null}
        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
      </div>
      {tabs || actions ? (
        <div className={styles.side}>
          {tabs}
          {actions}
        </div>
      ) : null}
    </header>
  );
}
